import { useEffect, useState, type ChangeEvent } from 'react'
import { api, type FormEquivalence, type FormSettings } from '../lib/api.ts'
import { hasEquivalence, withEquivalence, withoutEquivalence } from '../lib/formSettings.ts'
import { Button } from '../components/Button.tsx'
import { DataTable, type Column } from '../components/DataTable.tsx'
import { FormSettingsTabs } from '../components/FormSettingsTabs.tsx'
import { Select } from '../components/Select.tsx'
import { Tag } from '../components/Tag.tsx'
import { useConfirm } from '../components/Confirm.tsx'
import { useToast } from '../components/Toast.tsx'
import { icons } from '../components/icons.ts'

/**
 * The forms this pharmacy makes in a prescribed one's place (#607, ADR 0041).
 *
 * A table of rules, each added through a dialog and saved at once: the list is
 * the pharmacy's practice, short, and changed rarely, so there is no edit
 * held on the page waiting for a Salvar somebody can forget. The server
 * stores the whole list each time and checks it (one rule per pair, never a
 * form onto itself).
 */
export function FormEquivalences({
  accountId,
  onBack,
}: {
  accountId: string | undefined
  onBack: () => void
}) {
  const [settings, setSettings] = useState<FormSettings | undefined>()
  const [error, setError] = useState<string | undefined>()
  const [attempt, setAttempt] = useState(0)
  const [adding, setAdding] = useState(false)
  const [busy, setBusy] = useState(false)
  const toast = useToast()
  const confirm = useConfirm()

  useEffect(() => {
    if (accountId === undefined) return
    let live = true
    setError(undefined)
    void api
      .formSettings(accountId)
      .then((next) => {
        if (live) setSettings(next)
      })
      .catch((cause: unknown) => {
        if (live) setError(cause instanceof Error ? cause.message : 'falha ao carregar')
      })
    return () => {
      live = false
    }
  }, [accountId, attempt])

  const label = (form: string) => settings?.forms.find((one) => one.id === form)?.label ?? form
  const conditionLabel = (when: string | undefined) =>
    when === undefined
      ? 'Sempre'
      : (settings?.conditions.find((one) => one.id === when)?.label ?? when)

  async function save(next: readonly FormEquivalence[], done: string): Promise<boolean> {
    if (accountId === undefined || settings === undefined) return false
    setBusy(true)
    try {
      const { equivalences } = await api.saveFormEquivalences(accountId, next)
      setSettings({ ...settings, equivalences })
      toast.show(done, { tone: 'success' })
      return true
    } catch (cause) {
      toast.show(cause instanceof Error ? cause.message : 'falha ao salvar', { tone: 'error' })
      return false
    } finally {
      setBusy(false)
    }
  }

  async function remove(one: FormEquivalence) {
    if (settings === undefined) return
    const sure = await confirm({
      title: `Remover ${label(one.prescribed)} → ${label(one.keyed)}?`,
      message: `Nas próximas conferências, ${label(one.prescribed)} aviado como ${label(one.keyed)} volta a ser divergência.`,
      confirmLabel: 'Remover',
      tone: 'danger',
    })
    if (!sure) return
    await save(withoutEquivalence(settings.equivalences, one), 'Equivalência removida')
  }

  const rows = [...(settings?.equivalences ?? [])].sort(
    (a, b) =>
      label(a.prescribed).localeCompare(label(b.prescribed)) ||
      label(a.keyed).localeCompare(label(b.keyed)),
  )

  const columns: ReadonlyArray<Column<FormEquivalence>> = [
    { key: 'prescribed', header: 'Receita pede', render: (one) => label(one.prescribed) },
    { key: 'keyed', header: 'Pode ser aviado como', render: (one) => label(one.keyed) },
    {
      key: 'when',
      header: 'Quando',
      render: (one) => (
        <Tag tone={one.when === undefined ? 'neutral' : 'info'}>{conditionLabel(one.when)}</Tag>
      ),
    },
    {
      key: 'actions',
      header: '',
      className: 'w-px whitespace-nowrap text-right',
      headerClassName: 'w-px',
      render: (one) => (
        <Button
          size="small"
          variant="secondary"
          tone="danger"
          aria-label={`Remover ${label(one.prescribed)} → ${label(one.keyed)}`}
          title="Remover"
          leadingIcon={icons.remove}
          disabled={busy}
          onClick={() => void remove(one)}
        />
      ),
    },
  ]

  return (
    <div className="flex flex-col gap-5 p-5 pt-0">
      <div className="flex flex-wrap items-center gap-3">
        <Button
          variant="tertiary"
          tone="neutral"
          size="small"
          leadingIcon={icons.back}
          onClick={onBack}
        >
          Fila
        </Button>
        <h1 className="text-tH4 text-text-strong">Formas farmacêuticas</h1>
      </div>

      <FormSettingsTabs active="equivalences" />

      <div className="flex flex-wrap items-start justify-between gap-3">
        <p className="text-tP text-text-weak max-w-prose">
          Formas diferentes que esta farmácia avia no lugar da prescrita, sem que seja divergência.
          Cada uma vale numa direção só: comprimido aviado como cápsula não faz cápsula aviada como
          comprimido valer.
        </p>
        <Button
          variant="primary"
          tone="brand"
          size="small"
          leadingIcon={icons.add}
          disabled={settings === undefined}
          onClick={() => setAdding(true)}
        >
          Adicionar equivalência
        </Button>
      </div>

      <DataTable
        columns={columns}
        rows={rows}
        rowKey={(one) => `${one.prescribed}>${one.keyed}`}
        loading={settings === undefined && error === undefined}
        error={error}
        onRetry={() => setAttempt((n) => n + 1)}
        empty="Nenhuma equivalência: toda forma diferente da prescrita é divergência."
      />

      {adding && settings !== undefined && (
        <AddEquivalenceDialog
          settings={settings}
          busy={busy}
          onClose={() => setAdding(false)}
          onAdd={(one) =>
            void save(withEquivalence(settings.equivalences, one), 'Equivalência adicionada').then(
              (saved) => {
                if (saved) setAdding(false)
              },
            )
          }
        />
      )}
    </div>
  )
}

/** The three choices of a rule, and nothing else to fill. */
function AddEquivalenceDialog({
  settings,
  busy,
  onAdd,
  onClose,
}: {
  settings: FormSettings
  busy: boolean
  onAdd: (one: FormEquivalence) => void
  onClose: () => void
}) {
  const [prescribed, setPrescribed] = useState('')
  const [keyed, setKeyed] = useState('')
  const [when, setWhen] = useState('')
  const candidate: FormEquivalence = { prescribed, keyed, ...(when === '' ? {} : { when }) }
  const taken =
    prescribed !== '' && keyed !== '' && hasEquivalence(settings.equivalences, candidate)
  const ready = prescribed !== '' && keyed !== '' && prescribed !== keyed && !taken

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [onClose])

  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center bg-[rgba(0,9,51,0.35)] p-4"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose()
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="add-equivalence-title"
        className="bg-background-raised rounded-base shadow-raised border-stroke-weak flex w-full max-w-[30rem] flex-col gap-4 border p-5"
      >
        <div className="flex flex-col gap-1">
          <h2 id="add-equivalence-title" className="text-tPSemi text-text-strong">
            Adicionar equivalência
          </h2>
          <p className="text-tP text-text-weak">
            Quando a receita pedir esta forma e o orçamento tiver a outra, a conferência não aponta
            divergência.
          </p>
        </div>

        <div className="flex flex-col gap-3">
          <Select
            label="Receita pede"
            value={prescribed}
            onChange={(e: ChangeEvent<HTMLSelectElement>) => setPrescribed(e.target.value)}
          >
            <option value="" disabled>
              Escolha…
            </option>
            {settings.forms.map((form) => (
              <option key={form.id} value={form.id}>
                {form.label}
              </option>
            ))}
          </Select>
          <Select
            label="Pode ser aviado como"
            value={keyed}
            onChange={(e: ChangeEvent<HTMLSelectElement>) => setKeyed(e.target.value)}
            errorMessage={taken ? 'Esta combinação já tem uma equivalência.' : undefined}
          >
            <option value="" disabled>
              Escolha…
            </option>
            {settings.forms
              .filter((form) => form.id !== prescribed)
              .map((form) => (
                <option key={form.id} value={form.id}>
                  {form.label}
                </option>
              ))}
          </Select>
          <Select
            label="Quando"
            value={when}
            helperText="Uma condição é lida da forma e da posologia da receita."
            onChange={(e: ChangeEvent<HTMLSelectElement>) => setWhen(e.target.value)}
          >
            <option value="">Sempre</option>
            {settings.conditions.map((condition) => (
              <option key={condition.id} value={condition.id}>
                {condition.label}
              </option>
            ))}
          </Select>
        </div>

        <div className="flex justify-end gap-2">
          <Button variant="secondary" tone="neutral" size="small" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            variant="primary"
            tone="brand"
            size="small"
            disabled={!ready || busy}
            onClick={() => onAdd(candidate)}
          >
            {busy ? 'Adicionando…' : 'Adicionar'}
          </Button>
        </div>
      </div>
    </div>
  )
}
